import { Link } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";
import { useEffect, useState } from "react";
import Footer from "../components/Footer";
import FloatingIcons from "../components/FloatingIcons";
import ProgressMeter from "../components/ProgressMeter";
import { fetchStats, fetchContestStatsFromBackend } from "../services/api";
import { checkExtensionPresent, fetchContestStats } from "../services/extensionBridge";
import { getIdToken } from "firebase/auth";
import { auth } from "../firebase";
import RatingChart from "../components/RatingChart";
import ContestCounter from "../components/ContestCounter";

const features = [
  {
    icon: "⚡",
    colorRgb: "96,165,250",
    title: "Real Contest Problems",
    desc: "Every problem is sourced directly from actual LeetCode weekly and biweekly rounds — no fabricated questions, no guesswork.",
  },
  {
    icon: "🎯",
    colorRgb: "192,132,252",
    title: "Target Your Weak Spots",
    desc: "Filter by problem rating to see exactly where your skill breaks down and drill those ranges until they're strengths.",
  },
  {
    icon: "📈",
    colorRgb: "45,212,191",
    title: "Track Progress Across Sessions",
    desc: "Mark problems as solved and watch your coverage grow. Come back later and pick up exactly where you left off.",
  },
  {
    icon: "🧠",
    colorRgb: "244,114,182",
    title: "Build Pattern Recognition",
    desc: "Drilling problems in contest order (Q1→Q4) trains you to recognize problem types and estimate difficulty on the fly — just like top coders.",
  },
];

function HomePage({ user }) {
  const [siteStats, setSiteStats] = useState(null);
  const [contestData, setContestData] = useState(null);
  const [contestLoading, setContestLoading] = useState(false);

  const applyContestData = (data) => {
    if (!data) return;
    setContestData(data);
    if (data.contestsAttended != null)
      localStorage.setItem("lc_contests_attended", data.contestsAttended);
    if (data.contestRating != null)
      localStorage.setItem("lc_contest_rating", data.contestRating);
    if (data.ratingHistory?.length)
      localStorage.setItem("lc_rating_history", JSON.stringify(data.ratingHistory));
  };

  useEffect(() => {
    fetchStats(user?.uid).catch(() => null).then(setSiteStats);
  }, [user]);

  // Fetch contest stats: extension (live) → backend (server-side LeetCode fetch)
  useEffect(() => {
    if (!user || !userConnected) return;

    const lcUsername =
      localStorage.getItem("lc_username") || siteStats?.leetcodeUsername || null;

    if (lcUsername) localStorage.setItem("lc_username", lcUsername);

    const applyData = (data) => {
      if (!data) return;
      applyContestData(data);
    };

    setContestLoading(true);

    const tryExtension = lcUsername
      ? checkExtensionPresent().then((present) => {
          if (!present) return null;
          return fetchContestStats(lcUsername).then((r) =>
            r?.ok
              ? { contestsAttended: r.contestsAttended, contestRating: r.contestRating, ratingHistory: r.ratingHistory ?? null }
              : null
          );
        })
      : Promise.resolve(null);

    tryExtension
      .then((data) => {
        // Apply whatever the extension returned (may lack ratingHistory on old build)
        if (data) applyData(data);

        // If ratingHistory is still missing, fetch from backend regardless
        if (!data?.ratingHistory?.length) {
          return getIdToken(auth.currentUser, false)
            .then((tok) => fetchContestStatsFromBackend(tok))
            .then((res) => {
              applyData({
                contestsAttended: res.contestsAttended ?? data?.contestsAttended,
                contestRating:    res.contestRating    ?? data?.contestRating,
                ratingHistory:    res.ratingHistory    ?? null,
              });
            });
        }
      })
      .catch(() => {})
      .finally(() => setContestLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, siteStats]);

  const userConnected = localStorage.getItem("roundprep_lc_synced") === "true";
  const diff = siteStats?.difficulty;

  const contestsAttended =
    contestData?.contestsAttended ??
    siteStats?.user?.contestsAttended ??
    (localStorage.getItem("lc_contests_attended") != null
      ? parseInt(localStorage.getItem("lc_contests_attended"), 10)
      : null);
  const contestRating =
    contestData?.contestRating ??
    siteStats?.user?.contestRating ??
    (localStorage.getItem("lc_contest_rating") != null
      ? parseInt(localStorage.getItem("lc_contest_rating"), 10)
      : null);
  const ratingHistory =
    contestData?.ratingHistory ??
    (() => { try { return JSON.parse(localStorage.getItem("lc_rating_history") || "null"); } catch { return null; } })();

  const platformStats = [
    { value: siteStats ? siteStats.totalProblems.toLocaleString() : "—", label: "Contest Problems" },
    { value: siteStats ? siteStats.totalContests.toLocaleString()  : "—", label: "Rounds Covered"   },
    { value: "Q1→Q4", label: "Full Contest Flow" },
    { value: "Free",  label: "Always"            },
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour > 3 && hour < 12) return "Good Morning";
    if (hour > 11 && hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <>
      <FloatingIcons />
      <div className="relative pb-20">
        <div className="space-y-14">

          {/* ── 1. HERO ── */}
          <div className="relative space-y-8 pt-14 pb-4 animate-fade-up text-center">
            <div aria-hidden="true" style={{ position: "absolute", top: "-8%", left: "50%", transform: "translateX(-50%)", width: "min(1100px, 92vw)", height: 420, background: "radial-gradient(ellipse at center, rgba(96,165,250,0.14) 0%, rgba(192,132,252,0.10) 40%, transparent 72%)", filter: "blur(10px)", pointerEvents: "none", zIndex: -1 }} />

            <div>
              <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2.6rem, 6.5vw, 4.4rem)", fontWeight: 800, lineHeight: 1.08, letterSpacing: "-0.035em", margin: 0 }}>
                <span style={{ background: "linear-gradient(135deg, #e2e8f0 0%, rgba(226,232,240,0.65) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", display: "block", marginBottom: "0.12em" }}>
                  Stop Grinding Randomly.
                </span>
                <span style={{ background: "linear-gradient(135deg, #60a5fa 0%, #c084fc 55%, #2dd4bf 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", display: "block" }}>
                  <TypeAnimation sequence={["Start Winning Contests.", 3000, "Train Like the Top 1%.", 3000, "Climb Faster Than Ever.", 3000]} speed={52} repeat={Infinity} cursor={true} />
                </span>
              </h1>
            </div>

            <p style={{ fontSize: "1.2rem", color: "rgba(203,213,225,0.92)", fontFamily: "var(--font-display)", fontWeight: 450, lineHeight: 1.65, maxWidth: 680, margin: "0 auto" }}>
              RoundPrep turns real LeetCode contest history into a personalized training plan.
              Drill full rounds, target your weak rating ranges, and build the instincts that actually move your rank.
            </p>

            {/* CTA buttons (guests) or greeting (logged-in) */}
            {user ? (
              <div className="flex justify-center">
                <div className="glass inline-flex items-center gap-1 px-7 py-3" style={{ borderRadius: 999 }}>
                  <span className="text-sm" style={{ color: "rgba(203,213,225,0.85)", fontFamily: "var(--font-display)" }}>
                    {getGreeting()},{" "}
                    <span style={{ color: "var(--accent-blue)", fontWeight: 700 }}>{user.name.split(" ")[0]}</span>
                    {" "}— ready to climb today?
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Link to="/contest-drill" style={{ textDecoration: "none" }}>
                  <button style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "var(--font-display)", cursor: "pointer", letterSpacing: "-0.01em", boxShadow: "0 0 24px rgba(96,165,250,0.3)" }}>
                    Contest Drill →
                  </button>
                </Link>
                <Link to="/rating-lab" style={{ textDecoration: "none" }}>
                  <button style={{ padding: "12px 28px", borderRadius: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(203,213,225,0.9)", fontSize: 15, fontWeight: 600, fontFamily: "var(--font-display)", cursor: "pointer", letterSpacing: "-0.01em" }}>
                    Rating Lab
                  </button>
                </Link>
              </div>
            )}
          </div>

          {/* ── 2. SOCIAL PROOF ── */}
          <div className="glass animate-fade-up animate-fade-up-1" style={{ borderRadius: 18, padding: "18px 28px" }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {platformStats.map(({ value, label }) => (
                <div key={label} style={{ textAlign: "center", padding: "4px 0" }}>
                  <div style={{ fontSize: "1.55rem", fontWeight: 700, fontFamily: "var(--font-display)", background: "linear-gradient(135deg, #e2e8f0, rgba(226,232,240,0.65))", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-0.02em" }}>
                    {value}
                  </div>
                  <div style={{ fontSize: 11.5, color: "rgba(148,163,184,0.55)", fontFamily: "var(--font-display)", marginTop: 3, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 3. MY STATS (logged-in + connected only) ── */}
          {user && userConnected && (
            <div className="glass animate-fade-up animate-fade-up-1" style={{ borderRadius: 18, padding: "20px" }}>
              <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(148,163,184,0.35)", fontFamily: "var(--font-display)", marginBottom: 16, paddingLeft: 4 }}>
                My Stats
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>

                {/* Coverage speedometer */}
                <div style={{ borderRadius: 16, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", padding: "18px 12px 14px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(148,163,184,0.35)", fontFamily: "var(--font-display)" }}>Coverage</div>
                  <ProgressMeter easy={diff?.easy} medium={diff?.medium} hard={diff?.hard} connected={userConnected} />
                </div>

                {/* Rating chart */}
                <div style={{ borderRadius: 16, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", padding: "18px 14px 14px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  {contestLoading && !ratingHistory
                    ? <div style={{ color: "rgba(148,163,184,0.3)", fontSize: 18 }}>…</div>
                    : <RatingChart history={ratingHistory ?? []} currentRating={contestRating} />}
                </div>

                {/* Contest counter */}
                <div style={{ borderRadius: 16, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", padding: "18px 14px", minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {contestLoading && contestsAttended == null
                    ? <div style={{ color: "rgba(148,163,184,0.3)", fontSize: 18 }}>…</div>
                    : <ContestCounter count={contestsAttended} />}
                </div>

              </div>
            </div>
          )}

          {/* ── 4. PRODUCT CARDS ── */}
          <div className="grid md:grid-cols-2 gap-5 animate-fade-up animate-fade-up-2">

            <Link to="/contest-drill" className="glass glass-hover glass-blue p-7 text-left group block" style={{ textDecoration: "none" }}>
              <div style={{ position: "absolute", top: -70, right: -70, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(96,165,250,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
              <div className="flex justify-between items-start mb-5">
                <div style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(96,165,250,0.15)", border: "1px solid rgba(96,165,250,0.32)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: "0 0 16px rgba(96,165,250,0.2)" }}>⚡</div>
                <span className="badge badge-blue" style={{ marginTop: 4 }}>Q1 → Q4</span>
              </div>
              <h3 style={{ color: "#93c5fd", fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10 }}>Contest Drill</h3>
              <p style={{ color: "rgba(148,163,184,0.82)", fontSize: 14, lineHeight: 1.75 }}>
                Simulate real contests from start to finish. Practice problems in the exact order they appeared — Q1 through Q4 — and build the speed, instinct, and composure that separates the top finishers from the rest.
              </p>
              <div className="mt-5 flex items-center gap-1.5 text-xs font-semibold group-hover:gap-2.5 transition-all duration-300" style={{ color: "rgba(96,165,250,0.75)", fontFamily: "var(--font-display)" }}>
                Start drilling <span style={{ transition: "transform 0.25s" }} className="group-hover:translate-x-1">→</span>
              </div>
            </Link>

            <Link to="/rating-lab" className="glass glass-hover glass-green p-7 text-left group block" style={{ textDecoration: "none" }}>
              <div style={{ position: "absolute", top: -70, right: -70, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(45,212,191,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />
              <div className="flex justify-between items-start mb-5">
                <div style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(45,212,191,0.12)", border: "1px solid rgba(45,212,191,0.28)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: "0 0 16px rgba(45,212,191,0.18)" }}>🎯</div>
                <span className="badge badge-green" style={{ marginTop: 4 }}>By Rating</span>
              </div>
              <h3 style={{ color: "#5eead4", fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10 }}>Rating Lab</h3>
              <p style={{ color: "rgba(148,163,184,0.82)", fontSize: 14, lineHeight: 1.75 }}>
                Diagnose exactly where you're losing points. Filter problems by difficulty rating and drill the precise range where your skill breaks down — systematic improvement, not random grinding.
              </p>
              <div className="mt-5 flex items-center gap-1.5 text-xs font-semibold group-hover:gap-2.5 transition-all duration-300" style={{ color: "rgba(45,212,191,0.75)", fontFamily: "var(--font-display)" }}>
                Enter lab <span style={{ transition: "transform 0.25s" }} className="group-hover:translate-x-1">→</span>
              </div>
            </Link>
          </div>

          {/* ── 5. HOW IT WORKS ── */}
          <div className="glass p-8 text-left animate-fade-up animate-fade-up-3">
            <div className="flex items-center gap-3 mb-8">
              <div style={{ height: 1, flex: 1, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08))" }} />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "rgba(148,163,184,0.5)", fontFamily: "var(--font-display)" }}>How It Works</span>
              <div style={{ height: 1, flex: 1, background: "linear-gradient(90deg, rgba(255,255,255,0.08), transparent)" }} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { step: "01", color: "#60a5fa", title: "Pick a Contest Round", desc: "Browse 700+ real LeetCode weekly and biweekly contests. Start from any round you haven't fully solved." },
                { step: "02", color: "#c084fc", title: "Drill Q1 Through Q4", desc: "Solve problems in the exact contest order — no peeking at ratings. This mirrors real contest pressure and builds decision speed." },
                { step: "03", color: "#2dd4bf", title: "Plug Your Weak Spots", desc: "Use Rating Lab to find the exact difficulty tier where you miss problems. Drill it until it's a strength, then move up." },
              ].map(({ step, color, title, desc }) => (
                <div key={step} style={{ padding: "22px 20px", borderRadius: 16, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ fontSize: "2.2rem", fontWeight: 800, fontFamily: "var(--font-display)", color, marginBottom: 12, lineHeight: 1, opacity: 0.85 }}>{step}</div>
                  <div style={{ fontSize: "1rem", fontWeight: 600, fontFamily: "var(--font-display)", color: "rgba(226,232,240,0.92)", marginBottom: 8, letterSpacing: "-0.01em" }}>{title}</div>
                  <div style={{ fontSize: 13.5, color: "rgba(148,163,184,0.7)", lineHeight: 1.65 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 6. WHY ROUNDPREP ── */}
          <div className="glass p-8 text-left animate-fade-up animate-fade-up-3">
            <div className="flex items-center gap-3 mb-8">
              <div style={{ height: 1, flex: 1, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08))" }} />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "rgba(148,163,184,0.5)", fontFamily: "var(--font-display)" }}>Why RoundPrep</span>
              <div style={{ height: 1, flex: 1, background: "linear-gradient(90deg, rgba(255,255,255,0.08), transparent)" }} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {features.map(({ icon, colorRgb, title, desc }) => (
                <div key={title} className="flex items-start gap-4 py-4 px-5 rounded-2xl" style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.055)" }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: `rgba(${colorRgb},0.12)`, border: `1px solid rgba(${colorRgb},0.25)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0, marginTop: 2 }}>{icon}</div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 600, fontFamily: "var(--font-display)", color: "rgba(226,232,240,0.92)", marginBottom: 5, letterSpacing: "-0.01em" }}>{title}</div>
                    <div style={{ fontSize: 13.5, color: "rgba(148,163,184,0.72)", lineHeight: 1.65 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 7. BOTTOM CTA (guests only) ── */}
          {!user && (
            <div className="glass animate-fade-up animate-fade-up-3" style={{ borderRadius: 20, padding: "48px 32px", textAlign: "center" }}>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 12, color: "#e2e8f0" }}>
                Ready to climb your rating?
              </h2>
              <p style={{ fontSize: 15, color: "rgba(148,163,184,0.7)", fontFamily: "var(--font-display)", marginBottom: 28, maxWidth: 460, margin: "0 auto 28px" }}>
                Free forever. No signup required to start drilling.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Link to="/contest-drill" style={{ textDecoration: "none" }}>
                  <button style={{ padding: "13px 32px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "var(--font-display)", cursor: "pointer", letterSpacing: "-0.01em", boxShadow: "0 0 28px rgba(96,165,250,0.3)" }}>
                    Start Drilling — Free →
                  </button>
                </Link>
              </div>
            </div>
          )}

          <Footer />

        </div>
      </div>
    </>
  );
}


export default HomePage;
